import { useMemo, useState } from "react";

import { differenceInSeconds } from "date-fns";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router";

import { buildUrl } from "@/components/helpers/url-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { isXsQuery } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { CancelUnfulfilledOrderConfirmDialog } from "@/components/pieces/coaching/CancelUnpaidOrderConfirmDialog";
import { MeetingIcon } from "@/components/pieces/coaching/meetings/MeetingIcon";
import { ReviewIcon } from "@/components/pieces/coaching/reviews/ReviewIcon";
import { UnpaidOrder } from "@/components/pieces/coaching/UnpaidOrder";
import { StyledButton } from "@/components/pieces/link/LinkButton";
import { BLUE, YELLOW } from "@/css/constants";
import {
  FulfillmentStatusTypes,
  OrderPaymentStatus,
  ProductCategories,
  SessionsQuery,
} from "@/graphql";
import { OrderItem } from "@/types/products";
import { CoachingUser } from "@/types/users";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";
import { getWorkspaceHost } from "@/utils/workspaces";

import { BannerButton, DarkBanner } from "./DarkBanner";

type UnfulfilledOrderType = NonNullableArrayItem<
  NonNullable<SessionsQuery["currentUser"]>["unfulfilledOrders"]
>;

interface UnfulfilledOrdersProps {
  user: CoachingUser;
}

export function UnfulfilledOrders({ user }: UnfulfilledOrdersProps) {
  const [showingCancelDialogForId, setShowingCancelDialogForId] =
    useState<string>();

  const [endTime, setEndTime] = useState(new Date());

  const orders = ensureNonNullArray(user.unfulfilledOrders);
  const expiredMeetingIds = orders
    ?.filter((order) => {
      if (!order.cancelIfUnpaidBy) {
        return false;
      }
      const newTimeLeft = differenceInSeconds(
        new Date(order.cancelIfUnpaidBy),
        endTime,
      );
      return newTimeLeft < 0;
    })
    .map((m) => m.id);

  const unfulfilledOrders = useMemo(() => {
    return orders.filter((order) => {
      return !expiredMeetingIds.includes(order.id);
    });
  }, [orders, expiredMeetingIds]);

  const selectedOrder = useMemo(() => {
    return unfulfilledOrders.find(
      (order) => order.id === showingCancelDialogForId,
    );
  }, [unfulfilledOrders, showingCancelDialogForId]);

  return (
    <>
      {unfulfilledOrders.map((order, index) => (
        <UnfulfilledOrder
          key={index}
          order={order}
          setEndTime={setEndTime}
          setShowingCancelDialogForId={setShowingCancelDialogForId}
          expiredMeetingIds={expiredMeetingIds}
          index={index}
          clientId={user.id}
        />
      ))}
      <CancelUnfulfilledOrderConfirmDialog
        isOpen={!!showingCancelDialogForId}
        orderId={showingCancelDialogForId}
        customerId={user.id}
        onCancel={() => setShowingCancelDialogForId(undefined)}
        onConfirm={() => window.location.reload()}
        isPaidOrder={selectedOrder?.paymentStatus === OrderPaymentStatus.Paid}
      />
    </>
  );
}

interface UnfulfilledOrderProps {
  order: UnfulfilledOrderType;
  setEndTime: (endTime: Date) => void;
  setShowingCancelDialogForId: (id?: string) => void;
  expiredMeetingIds: string[];
  index: number;
  clientId: string;
}

function UnfulfilledOrder({
  order,
  setEndTime,
  setShowingCancelDialogForId,
  expiredMeetingIds,
  index,
  clientId,
}: UnfulfilledOrderProps) {
  const isXs = useMediaQuery(isXsQuery);
  const navigate = useNavigate();
  const { currentWorkspace, currentUser } = useAuth();

  const items = ensureNonNullArray(order.orderItems);

  const orderTypes = items.reduce(
    (acc, oi) => {
      const category = oi.product?.category;
      if (oi.fulfillmentStatus === FulfillmentStatusTypes.Pending) {
        if (oi.product?.category === ProductCategories.Review) {
          acc.reviews.push(oi);
        } else if (
          category === ProductCategories.Meeting ||
          category === ProductCategories.GroupMeeting
        ) {
          acc.meetings.push(oi);
        }
      }
      return acc;
    },
    {
      reviews: [],
      meetings: [],
    } as {
      reviews: OrderItem[];
      meetings: OrderItem[];
    },
  );

  if (!orderTypes.reviews.length && !orderTypes.meetings.length) {
    return null;
  }

  const checkoutUrl = buildUrl(`/checkout/${order.id}/next-steps`, {
    data: {
      clientId:
        currentUser.id !== clientId &&
        (currentUser.isSuperuser || currentUser.isAgent)
          ? clientId
          : undefined,
    },
  });

  function redirectToCheckout() {
    const { workspace } = order;
    const { urlSlug } = workspace || {};
    if (urlSlug && urlSlug !== currentWorkspace.urlSlug) {
      let host = getWorkspaceHost(WEB_APP, urlSlug);
      window.location.href = `${host}/checkout/${order.id}/next-steps`;
    } else {
      navigate(checkoutUrl);
    }
  }

  const icon = orderTypes.meetings.length ? (
    <MeetingIcon size={isXs ? 60 : 100} backgroundColor={BLUE} />
  ) : (
    <ReviewIcon size={isXs ? 60 : 100} backgroundColor={YELLOW} />
  );

  const nonCreditOrderItems = orderTypes.meetings.concat(orderTypes.reviews);
  const productMsg =
    nonCreditOrderItems.length === 1
      ? `for your ${nonCreditOrderItems[0].product?.name}`
      : orderTypes.meetings.length > 0
        ? "before your upcoming coaching session" +
          (orderTypes.meetings.length > 1 ? "s" : "")
        : "before we can start your review" +
          (orderTypes.reviews.length > 1 ? "s" : "");

  const actionMsg =
    order.paymentStatus === OrderPaymentStatus.Unpaid
      ? "Complete your purchase"
      : "We need more information";

  const message = `${actionMsg} ${productMsg}`;
  const buttonMsg =
    order.paymentStatus === OrderPaymentStatus.Unpaid
      ? "Complete Purchase"
      : "Provide Information";

  if (
    order.paymentStatus === OrderPaymentStatus.Unpaid &&
    orderTypes.meetings.length > 0
  ) {
    return (
      <div key={order.id}>
        <UnpaidOrder
          orderItems={orderTypes.meetings}
          cancelTime={new Date(order.cancelIfUnpaidBy || "")}
          onExpiredMeeting={() => {
            if (!expiredMeetingIds.includes(order.id)) {
              setEndTime(new Date());
            }
          }}
          onPressPayNow={redirectToCheckout}
          onPressCancel={() => setShowingCancelDialogForId(order.id)}
        />
      </div>
    );
  }

  return (
    <div key={order.id}>
      <DarkBanner
        py={[3, 4]}
        px={[3, 4]}
        className="round-corners"
        mt={index > 0 ? 24 : 0}
      >
        <Box>{icon}</Box>
        <Box ml={40} maxWidth={550}>
          <Box color={YELLOW} fontWeight={600}>
            IMPORTANT
          </Box>
          <Box fontSize={24} color="#FFF" fontWeight={600} mt={1}>
            {message}
          </Box>
          <Flex
            mt={3}
            alignItems="center"
            flexDirection={isXs ? "column" : "row"}
          >
            <BannerButton to={checkoutUrl}>{buttonMsg}</BannerButton>
            <Box ml={3}>
              <StyledButton
                onClick={() => setShowingCancelDialogForId(order.id)}
                style={{ width: "100%", height: "50px" }}
              >
                Cancel
              </StyledButton>
            </Box>
          </Flex>
        </Box>
      </DarkBanner>
    </div>
  );
}
